import React, { useState } from "react";
import { Link } from "react-router-dom";
import {
  MessageCircle,
  Phone,
  Mail,
  Ticket,
  ChevronDown,
  Headphones,
  Package,
  XCircle,
  RotateCcw,
  Download,
} from "lucide-react";

const CHANNELS = [
  {
    icon: MessageCircle,
    title: "Chat with us",
    sub: "Instant help. Available 24/7",
    cta: "Start Chat",
    href: "#chat",
  },
  {
    icon: Phone,
    title: "Call us",
    sub: "Speak to our team. 9 AM – 9 PM",
    cta: "Call Now",
    href: "tel:+911204567890",
  },
  {
    icon: Mail,
    title: "Email us",
    sub: "We'll get back to you within 24 hours",
    cta: "Send Email",
    href: "mailto:support@dyntra.com",
  },
  {
    icon: Ticket,
    title: "Raise a ticket",
    sub: "Submit an issue and track your request",
    cta: "Raise Ticket",
    href: "#ticket",
  },
];

const FAQS = [
  {
    q: "How can I track my order?",
    a: "Head to My Orders in your account. Every active order shows a Track Order button that opens the shipment timeline in real-time.",
  },
  {
    q: "What payment methods do you accept?",
    a: "UPI, all major credit/debit cards, Net Banking, and Cash on Delivery on eligible orders.",
  },
  {
    q: "How do I return or cancel an order?",
    a: "Orders can be cancelled from My Orders until they ship. For returns, open the delivered order and choose Return within 7 days of delivery.",
  },
  {
    q: "When will I receive my refund?",
    a: "Refunds are initiated the moment we approve your return. Bank credits typically land within 5–7 business days.",
  },
  {
    q: "How do I update my account details?",
    a: "Go to Profile → Personal Information and update any field. Changes are saved instantly.",
  },
];

const TICKETS = [
  { id: "#TK-84291", subject: "Delivery delay for order #OD12345", date: "May 20, 2025 · 2:30 PM", status: "Open" },
  { id: "#TK-83902", subject: "Refund not received", date: "May 18, 2025 · 11:15 AM", status: "Resolved" },
  { id: "#TK-83711", subject: "Product return request", date: "May 16, 2025 · 9:45 AM", status: "Open" },
];

const ORDER_ACTIONS = [
  { icon: Package, label: "Track Order", sub: "Real-time status" },
  { icon: XCircle, label: "Cancel Order", sub: "Request cancellation" },
  { icon: RotateCcw, label: "Return Items", sub: "Easy returns" },
  { icon: Download, label: "Invoice Copy", sub: "Download invoice" },
];

export default function Help() {
  const [open, setOpen] = useState(null);

  return (
    <div data-testid="account-help" className="space-y-6">
      <header>
        <h1 className="font-display text-2xl sm:text-3xl font-bold text-gray-900">
          Help &amp; Support
        </h1>
        <p className="text-sm text-gray-500 mt-1">
          We&apos;re here to help you. Choose a way to get in touch or find quick answers below.
        </p>
      </header>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        {CHANNELS.map((c) => {
          const Ico = c.icon;
          return (
            <a
              key={c.title}
              href={c.href}
              data-testid={`help-channel-${c.title.toLowerCase().replace(/\s+/g, "-")}`}
              className="rounded-2xl border border-gray-100 bg-white p-4 flex flex-col items-center text-center hover:border-[#4B2AAD] transition-colors"
            >
              <div className="h-11 w-11 rounded-2xl bg-[#F3EFFC] flex items-center justify-center">
                <Ico className="h-5 w-5 text-[#4B2AAD]" />
              </div>
              <p className="mt-3 text-sm font-bold text-gray-900">{c.title}</p>
              <p className="text-[11px] text-gray-500 mt-1">{c.sub}</p>
              <button className="mt-3 rounded-xl bg-[#F3EFFC] text-[#4B2AAD] text-xs font-bold px-3 py-1.5 hover:bg-[#4B2AAD] hover:text-white">
                {c.cta}
              </button>
            </a>
          );
        })}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <div className="rounded-2xl border border-gray-100 bg-white p-5">
          <h3 className="font-display text-base font-bold text-gray-900">
            Frequently Asked Questions
          </h3>
          <div className="mt-3 divide-y divide-gray-100">
            {FAQS.map((f, i) => {
              const isOpen = open === i;
              return (
                <div key={f.q} className="border-b border-gray-100 last:border-0">
                  <button
                    onClick={() => setOpen(isOpen ? null : i)}
                    data-testid={`faq-${i}`}
                    className="w-full py-3 flex items-center justify-between text-left focus:outline-none"
                  >
                    <span className="text-sm font-semibold text-gray-800 pr-4">
                      {f.q}
                    </span>
                    <ChevronDown
                      className={`h-4 w-4 text-gray-500 shrink-0 transition-transform ${
                        isOpen ? "rotate-180" : ""
                      }`}
                    />
                  </button>
                  {isOpen && (
                    <div className="pb-3 text-xs text-gray-600 leading-relaxed">
                      {f.a}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
          <Link
            to="/faqs"
            className="mt-4 inline-block text-xs font-semibold text-[#4B2AAD] hover:underline"
          >
            View all FAQs →
          </Link>
        </div>

        <div className="rounded-2xl border border-gray-100 bg-white p-5">
          <div className="flex items-center justify-between">
            <h3 className="font-display text-base font-bold text-gray-900">
              Recent Tickets
            </h3>
            <button className="text-xs font-semibold text-[#4B2AAD] hover:underline">
              View All
            </button>
          </div>
          <div className="mt-3 space-y-3">
            {TICKETS.map((t) => (
              <div
                key={t.id}
                className="rounded-2xl border border-gray-100 p-3 flex items-center gap-3"
              >
                <div className="h-9 w-9 rounded-full bg-[#F3EFFC] flex items-center justify-center shrink-0">
                  <Ticket className="h-4 w-4 text-[#4B2AAD]" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-xs font-bold text-[#4B2AAD]">{t.id}</p>
                  <p className="text-sm font-semibold text-gray-900 truncate">
                    {t.subject}
                  </p>
                  <p className="text-[11px] text-gray-500">{t.date}</p>
                </div>
                <span
                  className={`text-[10px] font-bold rounded-full px-2 py-0.5 border ${
                    t.status === "Open"
                      ? "bg-amber-50 text-amber-700 border-amber-100"
                      : "bg-emerald-50 text-emerald-700 border-emerald-100"
                  }`}
                >
                  {t.status}
                </span>
              </div>
            ))}
          </div>
          <button className="mt-3 w-full text-xs font-semibold text-[#4B2AAD] hover:underline">
            View all tickets →
          </button>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <div className="rounded-2xl border border-gray-100 bg-white p-5">
          <h3 className="font-display text-base font-bold text-gray-900">
            Order Help
          </h3>
          <p className="text-xs text-gray-500 mt-1">
            Quick actions to help you with your orders
          </p>
          <div className="mt-3 grid grid-cols-2 gap-3">
            {ORDER_ACTIONS.map((a) => {
              const Ico = a.icon;
              return (
                <div
                  key={a.label}
                  className="rounded-2xl bg-[#F5F3FB] p-3 flex items-start gap-2"
                >
                  <div className="h-8 w-8 rounded-full bg-white flex items-center justify-center shrink-0">
                    <Ico className="h-4 w-4 text-[#4B2AAD]" />
                  </div>
                  <div>
                    <p className="text-sm font-bold text-gray-900 leading-tight">
                      {a.label}
                    </p>
                    <p className="text-[10px] text-gray-500 leading-tight">
                      {a.sub}
                    </p>
                  </div>
                </div>
              );
            })}
          </div>
          <Link
            to="/account/orders"
            className="mt-4 inline-block text-xs font-semibold text-[#4B2AAD] hover:underline"
          >
            Go to My Orders →
          </Link>
        </div>

        <div className="rounded-2xl bg-gradient-to-br from-[#F3EFFC] via-[#F3EFFC] to-white border border-[#4B2AAD]/15 p-5 flex items-center gap-4">
          <div className="h-14 w-14 rounded-2xl bg-white flex items-center justify-center shrink-0 shadow-sm">
            <Headphones className="h-6 w-6 text-[#4B2AAD]" />
          </div>
          <div>
            <h3 className="font-display text-base font-bold text-gray-900">
              Contact Information
            </h3>
            <p className="text-xs text-gray-500">
              Our support team is always ready to help
            </p>
            <ul className="mt-2 text-xs text-gray-700 space-y-1">
              <li>📞 +91 98765 43210</li>
              <li>✉️ support@dyntra.com</li>
              <li>🕒 Mon–Sun, 9:00 AM – 9:00 PM</li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
}
